import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { ALFRED_MANAGE_PROMPT } from '@wix/ai-scheduling-assistant-constants';
import { Badge, Button, Card, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { connectorsTargetElementId } from '../../../lib/connectors-route';
import { queryKeys } from '../../../lib/query-client';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useSideChatFollow } from '../../../lib/use-side-chat-follow';
import { useAlfredConnect } from '../../../lib/use-alfred-connect';
import { ALFRED_OUTREACH_ROW, isAlfredOutreachTarget } from '../../../lib/outreach-alfred';
import { ALFRED_SETTINGS_PATH } from '../../../lib/alfred-settings';
import type { ConnectorStatus } from '../../../lib/connectors';
import { alfredSettingsEnabled } from '../../../lib/alfred-settings-flag';
import { AlfredSetupModal } from './AlfredSetupModal';
import styles from './ChannelsOutBody.module.css';

const CONNECTORS_PAGE = { page_name: 'settings-connectors' } as const;

/**
 * Alfred — the AI phone receptionist — as an Outreach row, in the Phone row's place (behind
 * `alfredInSymphonyOutreach`, see `lib/outreach-alfred.ts`). Drawn from the server's Alfred connector
 * entry. While this row is drawn the Tools tab leaves Alfred out (`listedOnTools`, on the same toggle read), so
 * this row is Alfred's one card:
 *
 *  - **Connected** → "Answering your business calls", a Connected badge, and **Manage**. Where it goes is
 *    `alfredSettingsInSymphony`, read at the click: ON opens Alfred's settings screen (`/alfred`); OFF opens
 *    the side chat IN PLACE with a fresh thread asking to customize Alfred or set up call forwarding — the
 *    skill's own "what's next". In place, not a route change: the reader is on a settings screen and stays
 *    on it, the way the opportunity cards open theirs (the phones push the chat screen instead).
 *  - **Not connected** → the pitch, and **Connect** — `useAlfredConnect`, which the settings screen's
 *    Connect shares: `alfredOnboardingInSymphony` ON opens the onboarding (`AlfredSetupModal`) over this
 *    screen — the number, then how to forward to it; OFF is the chat handoff through `useConnectConnector`,
 *    the same rail (and the same `connect_alfred` click) as Alfred's row on the Tools tab, opened IN PLACE
 *    for the same reason as Manage.
 *
 * It scrolls itself into view when it is the deeplink target: the page scrolls once when the route
 * changes, and this row only exists after the toggle and the connectors read have answered, so that
 * single scroll finds nothing. The old `channels-out/phone` link lands here too (`isAlfredOutreachTarget`),
 * which is why the scroll keys on the target slug itself: a shallow route between the two slugs re-runs it.
 *
 * The mark is `public/alfred.svg`, loaded from THIS app's mount rather than from the URL the server names
 * (`ALFRED_LOGO_URL`, which is the same file on the production host). Web owns the asset, so the row renders
 * the copy it ships with — which is also what lets a local build show the file it carries instead of the
 * deployed one. The Tools card and the phones keep the server's URL, since they do not ship the file.
 */
export function AlfredOutreachRow({ connector, target }: { connector: ConnectorStatus; target?: string }) {
  const highlighted = isAlfredOutreachTarget(target);
  const { t } = useTranslation();
  const basePath = useBasePath();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const refreshConnectors = () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors });
  const alfredConnect = useAlfredConnect();
  // Manage's chat arm and the chat arm of Connect finish in the side chat over this screen, so the row re-reads
  // while it is open, until its connected state flips. The onboarding arm refreshes the connectors itself.
  const followSideChat = useSideChatFollow(refreshConnectors, connector.connected);
  const rowRef = useRef<HTMLElement>(null);

  // Warmed here so the Manage click finds the gate already answered (`useAlfredConnect` warms Connect's).
  useEffect(() => {
    void alfredSettingsEnabled();
  }, []);

  useEffect(() => {
    if (!isAlfredOutreachTarget(target)) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    rowRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  }, [target]);

  const manage = () => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'outreach_alfred_manage' });
    void alfredSettingsEnabled().then((settings) => {
      if (settings) {
        void router.push(`${basePath}${ALFRED_SETTINGS_PATH}`);
      } else {
        followSideChat();
        openSideChat({ kind: 'new', message: ALFRED_MANAGE_PROMPT });
      }
    });
  };
  const connectAlfred = () => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'outreach_alfred_connect' });
    alfredConnect.start(connector, followSideChat);
  };

  const line = connector.connected ? t('connectors.channelsOut.alfred.connected') : t('connectors.channelsOut.alfred.description');

  return (
    <Card
      ref={rowRef}
      id={connectorsTargetElementId('channels-out', ALFRED_OUTREACH_ROW.slug)}
      variant="sunken"
      size="sm"
      className={`${styles.row}${highlighted ? ` ${styles.rowTarget}` : ''}`}
      data-testid="channels-out-alfred"
    >
      {/* The plate + text are one cluster 16 apart (Figma 20690-152934), matching the other rows. The plate
          is the shared translucent-neutral tile; a brand mark on a real connector, not the "coming soon" look. */}
      <span className={styles.left}>
        <span className={styles.glyph} aria-hidden>
          <img src={`${basePath}/alfred.svg`} alt="" width={16} height={16} className={styles.logo} />
        </span>
        <span className={styles.main}>
          <Typography variant="h4" component="span" className={styles.name}>
            {ALFRED_OUTREACH_ROW.label}
          </Typography>
          <Typography variant="p2" color="secondary" component="span" noWrap>
            {line}
          </Typography>
        </span>
      </span>
      {connector.connected && <Badge variant="connected" size="tiny" label={t('home.map.connected')} />}
      {connector.connected ? (
        <Button size="medium" variant="outlined" onClick={manage}>
          {t('common.manage')}
        </Button>
      ) : (
        <Button size="medium" variant="outlined" disabled={alfredConnect.busy} onClick={connectAlfred}>
          {t('common.connect')}
        </Button>
      )}
      <AlfredSetupModal open={alfredConnect.setupOpen} onClose={alfredConnect.closeSetup} />
    </Card>
  );
}

/**
 * Alfred's slot while the connectors read is in flight. The toggle is on, so the slot is Alfred's; what is
 * not known until the read answers is whether the account has the connector. So what IS known is drawn —
 * the mark on its plate and the name — and only what the read decides shimmers: the line and the CTA.
 * That way the tab does not draw seven rows and grow an eighth a second later, and the slot does not flip
 * from a grey ghost into a row either. Resolves to the row, or to nothing (no entry → no row, as ever).
 * Mirrored by iOS and Android `AlfredOutreachRowSkeleton`.
 */
export function AlfredOutreachRowSkeleton() {
  const basePath = useBasePath();
  return (
    <Card variant="sunken" size="sm" className={styles.row} aria-busy data-testid="channels-out-alfred-skeleton">
      <span className={styles.left}>
        <span className={styles.glyph} aria-hidden>
          <img src={`${basePath}/alfred.svg`} alt="" width={16} height={16} className={styles.logo} />
        </span>
        <span className={styles.main}>
          <Typography variant="h4" component="span" className={styles.name}>
            {ALFRED_OUTREACH_ROW.label}
          </Typography>
          <span className={`orion-shimmer ${styles.skeletonLine}`} aria-hidden />
        </span>
      </span>
      <span className={`orion-shimmer ${styles.skeletonCta}`} aria-hidden />
    </Card>
  );
}
